import { computed, shallowRef } from "vue";
import { assertNever } from "../lib/assertNever";
import { matchMaybe } from "../lib/maybe";
import { defineStore } from "pinia";
import { displayModeForShareRole } from "../lib/displayMode";
import {
  fetchShareSessionRole,
  isCloudReaderHosted,
} from "../lib/cloudEditorHost";
import { useDisplayModeStore } from "./useDisplayModeStore";
import {
  initialShareModeModel,
  updateShareMode,
  type ShareModeCmd,
  type ShareModeMsg,
  type ShareRole,
} from "./useShareModeStore.update";

export type { ShareRole };

/**
 * Collaborator / public share session (#100+). Forces the matching
 * DisplayMode and locks exit until the share session ends.
 *
 * TEA: pure Model/Msg in `useShareModeStore.update.ts`. Cmds run
 * synchronously here so lock/unlock is visible to callers immediately
 * (same as the pre-TEA Pinia mutators).
 */
export const useShareModeStore = defineStore("shareMode", () => {
  const model = shallowRef(initialShareModeModel);

  function runCmd(cmd: ShareModeCmd): void {
    const display = useDisplayModeStore();
    switch (cmd.kind) {
      case "lockDisplay":
        display.lockMode(true);
        display.setDisplayMode(displayModeForShareRole(cmd.role));
        return;
      case "unlockDisplay":
        display.lockMode(false);
        return;
      default:
        assertNever(cmd);
    }
  }

  function dispatch(msg: ShareModeMsg): void {
    const [next, cmds] = updateShareMode(model.value, msg);
    model.value = next;
    for (const cmd of cmds) {
      runCmd(cmd);
    }
  }

  const shareMode = computed(() => model.value.shareMode);
  const shareRole = computed(() => model.value.shareRole);

  function enterShareMode(role: ShareRole = "read"): void {
    dispatch({ type: "Enter", role });
  }

  function exitShareMode(): void {
    dispatch({ type: "Exit" });
  }

  /** Auto-activate when the SPA is mounted under `/reader`. */
  async function syncFromHost(): Promise<void> {
    if (typeof window === "undefined") return;
    if (isCloudReaderHosted() === false || model.value.shareMode) return;
    const roleM = await fetchShareSessionRole();
    const role = matchMaybe(roleM, {
      nothing: () => "read" as ShareRole,
      just: (r) => r,
    });
    enterShareMode(role);
  }

  return {
    shareMode,
    shareRole,
    enterShareMode,
    exitShareMode,
    syncFromHost,
  };
});
